import { SearchContext, ContextualIcon, SmartTag, MemberContext } from '@/types/search';
import { Member } from '@/types/member';

// Mapping des mots-clés vers des icônes contextuelles
const CONTEXTUAL_MAPPINGS: Record<string, SearchContext> = {
  // Finance & Investment
  'financement': { intent: 'financing', keywords: ['financement', 'investissement', 'capital', 'funding'], icon: '💰', color: 'green', description: 'Recherche de financement' },
  'investissement': { intent: 'investment', keywords: ['investissement', 'investisseur', 'capital-risque', 'vc'], icon: '📈', color: 'blue', description: 'Opportunités d\'investissement' },
  'levée': { intent: 'fundraising', keywords: ['levée', 'fonds', 'round', 'série'], icon: '🚀', color: 'purple', description: 'Levée de fonds' },
  
  // Business & Partnership
  'partenariat': { intent: 'partnership', keywords: ['partenariat', 'collaboration', 'alliance'], icon: '🤝', color: 'orange', description: 'Recherche de partenaires' },
  'startup': { intent: 'startup', keywords: ['startup', 'entrepreneur', 'innovation'], icon: '🚀', color: 'purple', description: 'Écosystème startup' },
  'mentor': { intent: 'mentoring', keywords: ['mentor', 'conseil', 'coaching'], icon: '🎯', color: 'indigo', description: 'Mentorat et conseils' },
  
  // Technology
  'développeur': { intent: 'development', keywords: ['développeur', 'dev', 'programmeur', 'code'], icon: '💻', color: 'cyan', description: 'Développement tech' },
  'ia': { intent: 'ai', keywords: ['ia', 'intelligence artificielle', 'machine learning', 'ai'], icon: '🤖', color: 'violet', description: 'Intelligence artificielle' },
  'tech': { intent: 'technology', keywords: ['tech', 'technologie', 'digital', 'numérique'], icon: '⚡', color: 'yellow', description: 'Technologies' },
  
  // Professional Services
  'consultant': { intent: 'consulting', keywords: ['consultant', 'conseil', 'expertise'], icon: '💡', color: 'amber', description: 'Conseil et expertise' },
  'marketing': { intent: 'marketing', keywords: ['marketing', 'communication', 'brand'], icon: '📢', color: 'pink', description: 'Marketing et communication' },
  'design': { intent: 'design', keywords: ['design', 'ux', 'ui', 'créatif'], icon: '🎨', color: 'rose', description: 'Design et créativité' },
  
  // Industries
  'fintech': { intent: 'fintech', keywords: ['fintech', 'finance', 'blockchain'], icon: '🏦', color: 'emerald', description: 'Technologies financières' },
  'healthtech': { intent: 'healthtech', keywords: ['healthtech', 'santé', 'médical'], icon: '🏥', color: 'red', description: 'Technologies de santé' },
  'edtech': { intent: 'edtech', keywords: ['edtech', 'éducation', 'formation'], icon: '📚', color: 'blue', description: 'Technologies éducatives' },
  
  // Location-based
  'paris': { intent: 'location', keywords: ['paris', 'île-de-france'], icon: '🗼', color: 'slate', description: 'Paris et région' },
  'lyon': { intent: 'location', keywords: ['lyon', 'rhône'], icon: '🏙️', color: 'slate', description: 'Lyon et région' },
  'remote': { intent: 'remote', keywords: ['remote', 'télétravail', 'distance'], icon: '🌍', color: 'teal', description: 'Travail à distance' },
};

// Algorithmes pour générer des smart tags
export class SmartTagGenerator {
  static generateMemberTags(member: Member, allMembers: Member[], userActivity?: any): SmartTag[] {
    const tags: SmartTag[] = [];
    
    // Tag "Hot" - basé sur la popularité et l'activité récente
    const hotScore = this.calculateHotScore(member, allMembers);
    if (hotScore > 75) {
      tags.push({
        id: 'hot',
        label: 'Hot',
        type: 'activity',
        score: hotScore,
        icon: '🔥',
        color: 'red',
        description: 'Profil très populaire en ce moment',
        isActive: true
      });
    }
    
    // Tag "Rising Star" - membre récent avec forte croissance
    const risingScore = this.calculateRisingScore(member);
    if (risingScore > 70) {
      tags.push({
        id: 'rising',
        label: 'Rising Star',
        type: 'activity',
        score: risingScore,
        icon: '⭐',
        color: 'yellow',
        description: 'Nouveau membre avec forte progression',
        isActive: true
      });
    }
    
    // Tag "Expert" - basé sur les compétences et l'expérience
    const expertiseScore = this.calculateExpertiseScore(member);
    if (expertiseScore > 80) {
      tags.push({
        id: 'expert',
        label: 'Expert',
        type: 'algorithmic',
        score: expertiseScore,
        icon: '🎓',
        color: 'purple',
        description: 'Expertise reconnue dans son domaine',
        isActive: true
      });
    }
    
    // Tag "Fast Responder" - taux de réponse élevé
    const responseScore = this.calculateResponseScore(member);
    if (responseScore > 85) {
      tags.push({
        id: 'fast-responder',
        label: 'Fast Responder',
        type: 'behavioral',
        score: responseScore,
        icon: '⚡',
        color: 'green',
        description: 'Répond rapidement aux messages',
        isActive: true
      });
    }
    
    // Tag "Perfect Match" - compatibilité élevée avec l'utilisateur
    const matchScore = this.calculateMatchScore(member, userActivity);
    if (matchScore > 90) {
      tags.push({
        id: 'perfect-match',
        label: 'Perfect Match',
        type: 'compatibility',
        score: matchScore,
        icon: '💎',
        color: 'blue',
        description: 'Compatibilité exceptionnelle avec vos intérêts',
        isActive: true
      });
    }
    
    // Tag "Connector" - beaucoup de connexions
    const networkScore = this.calculateNetworkScore(member, allMembers);
    if (networkScore > 75) {
      tags.push({
        id: 'connector',
        label: 'Connector',
        type: 'algorithmic',
        score: networkScore,
        icon: '🌐',
        color: 'indigo',
        description: 'Excellentes connexions dans le réseau',
        isActive: true
      });
    }
    
    return tags.slice(0, 3); // Limiter à 3 tags max par membre
  }
  
  private static calculateHotScore(member: Member, allMembers: Member[]): number {
    let score = 0;
    
    // Score basé sur la complétude du profil
    const joinDateBoost = member.join_date ? 10 : 0;
    const skillsBoost = member.skills?.length ? member.skills.length * 5 : 0;
    const bioBoost = member.bio && member.bio.length > 50 ? 15 : 0;
    const linkedinBoost = member.linkedin ? 10 : 0;
    
    score = joinDateBoost + skillsBoost + bioBoost + linkedinBoost;
    return Math.min(100, score);
  }
  
  private static calculateRisingScore(member: Member): number {
    const isRecent = member.join_date && 
      new Date(member.join_date).getTime() > Date.now() - (90 * 24 * 60 * 60 * 1000);
    
    if (!isRecent) return 0;
    
    // Score basé sur la complétude du profil pour les nouveaux membres
    let score = 40; // Base pour être récent
    if (member.skills?.length) score += member.skills.length * 5;
    if (member.bio) score += 15;
    if (member.linkedin) score += 10;
    return Math.min(100, score);
  }
  
  private static calculateExpertiseScore(member: Member): number {
    let score = 0;
    
    // Boost for role seniority
    const seniorRoles = ['senior', 'lead', 'principal', 'director', 'ceo', 'cto', 'founder'];
    if (seniorRoles.some(role => member.role.toLowerCase().includes(role))) {
      score += 30;
    }
    
    // Boost for number of skills
    score += Math.min(30, (member.skills?.length || 0) * 3);
    
    // Boost for achievements
    if (member.achievements) score += 20;
    
    // Boost for bio length (indicates detailed profile)
    if (member.bio && member.bio.length > 100) score += 20;
    
    return Math.min(100, score);
  }
  
  private static calculateResponseScore(member: Member): number {
    // Pas de données réelles — retourne 0 (pas de tag "Fast Responder" affiché)
    return 0;
  }
  
  private static calculateMatchScore(member: Member, userActivity?: any): number {
    // Pas de données réelles — retourne 0 (pas de tag "Perfect Match" affiché)
    return 0;
  }
  
  private static calculateNetworkScore(member: Member, allMembers: Member[]): number {
    // Score basé sur les données réelles du profil
    let score = 0;
    const skillsBoost = member.skills?.length ? Math.min(30, member.skills.length * 3) : 0;
    const interestsBoost = member.interests?.length ? Math.min(20, member.interests.length * 4) : 0;
    const linkedinBoost = member.linkedin ? 15 : 0;
    score = skillsBoost + interestsBoost + linkedinBoost;
    return Math.min(100, score);
  }
}

export class ContextualSearchEngine {
  static parseSearchIntent(searchTerm: string): SearchContext | null {
    const term = searchTerm.toLowerCase().trim();
    
    // Recherche exacte d'abord
    for (const [key, context] of Object.entries(CONTEXTUAL_MAPPINGS)) {
      if (term.includes(key)) {
        return context;
      }
    }
    
    // Recherche dans les keywords
    for (const context of Object.values(CONTEXTUAL_MAPPINGS)) {
      if (context.keywords.some(keyword => term.includes(keyword))) {
        return context;
      }
    }
    
    return null;
  }
  
  static generateContextualIcons(searchTerm: string): ContextualIcon[] {
    const icons: ContextualIcon[] = [];
    const term = searchTerm.toLowerCase();
    
    for (const [key, context] of Object.entries(CONTEXTUAL_MAPPINGS)) {
      let matchScore = 0;
      
      // Score basé sur la correspondance exacte
      if (term.includes(key)) {
        matchScore = 100;
      } else {
        // Score basé sur les mots-clés
        for (const keyword of context.keywords) {
          if (term.includes(keyword)) {
            matchScore = Math.max(matchScore, 80);
          }
        }
      }
      
      if (matchScore > 0) {
        icons.push({
          keyword: key,
          icon: context.icon,
          color: context.color,
          label: context.description,
          matchScore
        });
      }
    }
    
    return icons.sort((a, b) => b.matchScore - a.matchScore).slice(0, 5);
  }
  
  static generateMemberContext(member: Member, searchTerm: string, allMembers: Member[]): MemberContext {
    const smartTags = SmartTagGenerator.generateMemberTags(member, allMembers);
    const contextualIcons = this.generateMemberContextualIcons(member, searchTerm);
    
    return {
      memberId: member.id,
      smartTags,
      contextualIcons,
      relevanceScore: this.calculateRelevanceScore(member, searchTerm),
      lastActivity: undefined, // Pas de données réelles disponibles
      responseRate: 0, // Pas de données réelles
      popularityScore: 0 // Pas de données réelles
    };
  }
  
  private static generateMemberContextualIcons(member: Member, searchTerm: string): ContextualIcon[] {
    const icons: ContextualIcon[] = [];
    
    // Analyse du profil membre pour des icônes contextuelles
    const memberText = [
      member.name,
      member.role,
      member.company,
      member.bio || '',
      ...(member.skills || []),
      ...(member.tags || [])
    ].join(' ').toLowerCase();
    
    for (const [key, context] of Object.entries(CONTEXTUAL_MAPPINGS)) {
      let matchScore = 0;
      
      for (const keyword of context.keywords) {
        if (memberText.includes(keyword)) {
          matchScore += 20;
        }
      }
      
      // Bonus si le terme de recherche correspond aussi
      if (searchTerm.toLowerCase().includes(key)) {
        matchScore += 50;
      }
      
      if (matchScore > 0) {
        icons.push({
          keyword: key,
          icon: context.icon,
          color: context.color,
          label: context.description,
          matchScore: Math.min(100, matchScore)
        });
      }
    }
    
    return icons.sort((a, b) => b.matchScore - a.matchScore).slice(0, 3);
  }
  
  private static calculateRelevanceScore(member: Member, searchTerm: string): number {
    if (!searchTerm) return 50;
    const term = searchTerm.toLowerCase();
    let score = 0;
    if (member.name.toLowerCase().includes(term)) score += 40;
    if (member.role.toLowerCase().includes(term)) score += 30;
    if (member.company.toLowerCase().includes(term)) score += 20;
    if (member.skills?.some(s => s.toLowerCase().includes(term))) score += 30;
    return Math.min(100, score);
  }
}

export const searchSuggestions = [
  { text: 'développeurs React à Paris', icon: '💻', category: 'Tech', popularity: 95 },
  { text: 'experts en IA', icon: '🤖', category: 'Tech', popularity: 90 },
  { text: 'investisseurs série A', icon: '💰', category: 'Finance', popularity: 85 },
  { text: 'startup fintech', icon: '🏦', category: 'Business', popularity: 80 },
  { text: 'mentors expérimentés', icon: '🎯', category: 'Mentorat', popularity: 75 },
  { text: 'partenaires marketing', icon: '📢', category: 'Business', popularity: 70 },
  { text: 'designers UX/UI', icon: '🎨', category: 'Design', popularity: 65 },
  { text: 'consultants stratégie', icon: '💡', category: 'Conseil', popularity: 60 }
];